val defaultVs = """
   attribute vec4 position;
        attribute vec4 v_color;
        attribute float vertexID;
        
        uniform vec2 resolution;
        uniform vec4 color;
        uniform mat4 matrix;
        uniform float time;
        
        varying vec4 vertex_color;
        void main() {
          gl_Position = matrix * position;
          float offset = sin(time*0.005) * 0.5 + 0.5;
         vertex_color = vec4(vec3(1.0)*offset,1.0);
        }     
""".trimIndent()

val vs = """
   attribute vec4 position;
        attribute vec4 v_color;
        attribute float vertexID;
        
        uniform vec2 resolution;
        uniform float mode;
        uniform vec4 color;
        uniform mat4 matrix;
        uniform float chimeID;
        uniform float numChimes;
        uniform float time;
       vec3 palette( in float t, in vec3 a, in vec3 b, in vec3 c, in vec3 d )
            { return a + b*cos( 6.28318*(c*t+d) ); } 
        varying vec4 vertex_color;
        //	Simplex 3D Noise 
        //	by Ian McEwan, Ashima Arts
        //
        vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
        vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}

        float snoise(vec3 v){ 
          const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
          const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

        // First corner
          vec3 i  = floor(v + dot(v, C.yyy) );
          vec3 x0 =   v - i + dot(i, C.xxx) ;

        // Other corners
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min( g.xyz, l.zxy );
          vec3 i2 = max( g.xyz, l.zxy );

          //  x0 = x0 - 0. + 0.0 * C 
          vec3 x1 = x0 - i1 + 1.0 * C.xxx;
          vec3 x2 = x0 - i2 + 2.0 * C.xxx;
          vec3 x3 = x0 - 1. + 3.0 * C.xxx;

        // Permutations
          i = mod(i, 289.0 ); 
          vec4 p = permute( permute( permute( 
                     i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                   + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
                   + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

        // Gradients
        // ( N*N points uniformly over a square, mapped onto an octahedron.)
          float n_ = 1.0/7.0; // N=7
          vec3  ns = n_ * D.wyz - D.xzx;

          vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)

          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

          vec4 x = x_ *ns.x + ns.yyyy;
          vec4 y = y_ *ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);

          vec4 b0 = vec4( x.xy, y.xy );
          vec4 b1 = vec4( x.zw, y.zw );

          vec4 s0 = floor(b0)*2.0 + 1.0;
          vec4 s1 = floor(b1)*2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));

          vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
          vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

          vec3 p0 = vec3(a0.xy,h.x);
          vec3 p1 = vec3(a0.zw,h.y);
          vec3 p2 = vec3(a1.xy,h.z);
          vec3 p3 = vec3(a1.zw,h.w);

        //Normalise gradients
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
          p0 *= norm.x;
          p1 *= norm.y;
          p2 *= norm.z;
          p3 *= norm.w;

        // Mix final noise value
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                        dot(p2,x2), dot(p3,x3) ) );
        }
        vec3 componentA(float pt){
            vec3 outcol = vec3(0.5);
            vec3 mode2 = vec3(0.5);
            vec3 mode3 = vec3(0.8,0.5,0.4);
            vec3 mode4 = vec3(0.5);
            outcol = mix(outcol, mode2, smoothstep(0.25,0.50,pt));
            outcol = mix(outcol, mode3, smoothstep(0.5,0.75,pt));
            outcol = mix(outcol, mode4, smoothstep(0.75,1.00,pt));
            return outcol;
        }
        vec3 componentB(float pt){
            vec3 outcol = vec3(0.5);
            vec3 mode2 = vec3(0.5);
            vec3 mode3 = vec3(0.2,0.4,0.2);
            vec3 mode4 = vec3(0.5);
            outcol = mix(outcol, mode2, smoothstep(0.25,0.50,pt));
            outcol = mix(outcol, mode3, smoothstep(0.5,0.75,pt));
            outcol = mix(outcol, mode4, smoothstep(0.75,1.00,pt));
            return outcol;
        }
        vec3 componentC(float pt){
            vec3 outcol = vec3(1.0,0.7,0.4);
            vec3 mode2 = vec3(1.0);
            vec3 mode3 = vec3(2.0,1.0,1.0);
            vec3 mode4 = vec3(1.0,1.0,0.5);
            outcol = mix(outcol, mode2, smoothstep(0.25,0.50,pt));
            outcol = mix(outcol, mode3, smoothstep(0.5,0.75,pt));
            outcol = mix(outcol, mode4, smoothstep(0.75,1.00,pt));
            return outcol;
        }
        vec3 componentD(float pt){
            vec3 outcol = vec3(0.0,0.15,0.2);
            vec3 mode2 = vec3(0.0,0.1,0.2);
            vec3 mode3 = vec3(0.0,0.25,0.25);
            vec3 mode4 = vec3(0.8,0.9,0.3);
            outcol = mix(outcol, mode2, smoothstep(0.25,0.50,pt));
            outcol = mix(outcol, mode3, smoothstep(0.5,0.75,pt));
            outcol = mix(outcol, mode4, smoothstep(0.75,1.00,pt));
            return outcol;
        }
        void main() {
          gl_Position = matrix * position;
          float xid = v_color.x;
          float yid = v_color.y;
          float n = snoise(vec3(
          xid * 0.25,
          yid*0.25,
          time*0.001
          ));
         vec3 col = palette (
            (chimeID*0.1) + (time*0.005) + n, 
            componentA(mode),
            componentB(mode),
            componentC(mode),
            componentD(mode)
          );
          float offset = sin(time*0.005) * 0.5 + 0.5;
         vertex_color = vec4(col+((1.0-xid)*(0.5*offset)),1.0);
         //vertex_color = vec4(col,1.0);
        }     
""".trimIndent()

val fs = """
      precision highp float;
        varying vec4 vertex_color;
        
        void main() {
          gl_FragColor = vertex_color;
        } 
""".trimIndent()